import React from 'react';
import { useNavigate } from 'react-router-dom';
import './Policies.css';

const PrivacyPolicy: React.FC = () => {
    const navigate = useNavigate();

    return (
        <div className="policy-container">
            <button className="back-button" onClick={() => navigate(-1)}>
                ← Back
            </button>
            <h1>Privacy Policy</h1>
            <p className="policy-intro">Last Updated: January 5, 2025</p>

            <section className="privacy-section">
                <h2 className="section-title">Introduction</h2>

                <div className="content-block">
                    <p>
                        Fervoir ("we," "our," "us") is committed to protecting your privacy in accordance with applicable data protection laws, including the GDPR, CCPA, and store-specific requirements. This Privacy Policy explains what data we collect, how we use it, and the rights you have regarding your information.
                    </p>
                </div>

                <h2 className="section-title">Data Collection & Permissions</h2>
                <div className="content-block">
                    <p>Fervoir may request the following permissions solely for app functionality, user experience, or optional features:</p>

                    <h4 className="sub-sub-title">Location Access</h4>
                    <ul className="list">
                        <li><strong>Purpose:</strong> To enable nearby match suggestions.</li>
                        <li><strong>Usage:</strong> Only when granted and actively in use.</li>
                        <li><strong>Control:</strong> Can be disabled anytime in your settings.</li>
                    </ul>

                    <h4 className="sub-sub-title">Camera & Storage</h4>
                    <ul className="list">
                        <li><strong>Purpose:</strong> Profile photos, media sharing.</li>
                        <li><strong>Usage:</strong> Accessed only when required (e.g., uploading media).</li>
                        <li><strong>Control:</strong> User-controlled via system permissions.</li>
                    </ul>

                    <h4 className="sub-sub-title">Microphone</h4>
                    <ul className="list">
                        <li><strong>Purpose:</strong> For voice messages and calls.</li>
                        <li><strong>Usage:</strong> Live audio, not stored unless explicitly initiated.</li>
                        <li><strong>Control:</strong> Optional; can be revoked.</li>
                    </ul>

                    <h4 className="sub-sub-title">Internet & Network</h4>
                    <ul className="list">
                        <li><strong>Purpose:</strong> For sending messages, real-time updates, media sync.</li>
                        <li><strong>Usage:</strong> No tracking of personal network data.</li>
                    </ul>

                    <h4 className="sub-sub-title">Notifications</h4>
                    <ul className="list">
                        <li><strong>Purpose:</strong> Inform users of matches, messages, events.</li>
                        <li><strong>Usage:</strong> Requires user consent (Android 13+).</li>
                    </ul>

                    <p className="mt-2">
                        Permissions such as <strong>CAMERA</strong>, <strong>RECORD_AUDIO</strong>, <strong>ACCESS_FINE_LOCATION</strong>, <strong>READ/WRITE_EXTERNAL_STORAGE</strong>, and <strong>POST_NOTIFICATIONS</strong> are declared with transparency. No permission is used for any hidden or background tracking. You can modify or revoke any permission at any time via your device settings.
                    </p>
                </div>

                <h2 className="section-title">Data Sharing & Third-Party Services</h2>
                <div className="content-block">
                    <p>
                        Fervoir does not sell your data. We may use vetted third-party services to deliver app functionality:
                    </p>
                    <ul className="list">
                        <li><strong>EmailJS:</strong> For user support/contact form submissions.</li>
                        <li><strong>Firebase:</strong> For push notifications and analytics (non-identifiable data only).</li>
                    </ul>
                    <p className="mt-2">
                        All third parties are GDPR/CCPA compliant. Your data is processed only for app operation and not for advertising resale.
                    </p>
                </div>

                <h2 className="section-title">User Rights & Account Control</h2>
                <div className="content-block">
                    <ul className="list">
                        <li>You may request access, correction, or deletion of your personal data.</li>
                        <li>You may delete your account at any time within the app or by emailing us.</li>
                        <li>You may opt out of marketing emails and analytics tracking.</li>
                    </ul>
                    <p className="mt-2">
                        Contact us at <a href="mailto:paulfortuneltd@gmail.com" className="email">paulfortuneltd@gmail.com</a> to exercise your rights.
                    </p>
                </div>

                <h2 className="section-title">Security Measures</h2>
                <div className="content-block">
                    <ul className="list">
                        <li>All user data is encrypted using SSL/TLS.</li>
                        <li>Only authorized personnel may access system data.</li>
                        <li>No security system is perfect; please use strong passwords and stay alert.</li>
                    </ul>
                </div>

                <h2 className="section-title">Age Restriction</h2>
                <div className="content-block">
                    <p>
                        Fervoir is for users 18 years and older. If you believe a minor is using the platform, please notify us.
                    </p>
                </div>

                <h2 className="section-title">Updates to This Policy</h2>
                <div className="content-block">
                    <p>
                        We may update this Privacy Policy periodically.
                    </p>
                    <ul className="list">
                        <li>Major changes will be communicated via app and/or email.</li>
                        <li>Continued use of the app means acceptance of the updated terms.</li>
                    </ul>
                </div>

                <h2 className="section-title">Contact Us</h2>
                <div className="content-block">
                    <p>
                        Questions, feedback, or requests may be sent to:
                    </p>
                    <p>
                        Email: <a href="mailto:paulfortuneltd@gmail.com" className="email">paulfortuneltd@gmail.com</a>
                    </p>
                </div>
            </section>
        </div>
    );
};

export default PrivacyPolicy;